import { appConfigStore } from 'Models/AppConfigStore';
import React, { useEffect } from 'react';
import { observer } from 'mobx-react';

import useDeviceDetect from 'Hooks/useDeviceDetect';
import usePassengerTypes from 'Hooks/usePassengerTypes';
import { LocationEntity, RouteEntity } from 'Models/Entities';
import { TripDetails } from 'Modules/TripDetails/TripDetails';
import { journeyElementsStore } from 'Global/JourneyElements/JourneyElementsStore';
import { useLocation, useNavigate } from 'react-router-dom';
import { BookingWizardData } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/BaseBookingWizardData';
import { PassengerBookingWizardData } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/PassengerBookingWizardData';
import { VehicleBookingWizardData } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/VehicleBookingWizardData';
import { useBookingWizardDataStore } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardDataStore';
import { PassengerTypeInputs } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/Sidebar/FerryFilters/PassengerDetails/PassengerTypeInputs';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import { LottieSpinner } from 'Views/Components/_HumanWritten/Lottie/LottieSpinner';
import If from 'Views/Components/If/If';
import { useHomeStore } from '../Stores/HomeStore';
import { FreightIcon } from 'Views/Components/_HumanWritten/Icon/IconHelpers';
import { Button, Colors, Display, Sizes } from 'Views/Components/Button/Button';
import { FormattedMessage } from 'react-intl';
import { isNotNullOrUndefined, stringIsEmpty, stringNotEmpty } from 'Util/TypeGuards';
import { themeStore } from 'Models/ThemeStore';
import RouteMap from 'Views/Components/RouteMap/RouteMap';
import { trackTripSearch } from 'Util/_HumanWritten/Analytics/analyticsEvents';
import { analyticsBookingType, analyticsRouteLabel } from 'Util/_HumanWritten/Analytics/analyticsBookingFields';
import useStore from '../../../Hooks/useStore';

/** Format a Date as YYYY-MM-DD for analytics, guarding against invalid/missing dates. */
const toAnalyticsDate = (date?: Date): string | undefined =>
	date instanceof Date && !Number.isNaN(date.getTime()) ? date.toISOString().slice(0, 10) : undefined;

export interface HomeSearchProps {
	type: string;
}

/**
 * Feature: Multi-stop - use stop locations
 */
function HomeSearch({ type }: HomeSearchProps) {
	const navigate = useNavigate();
	const location = useLocation();
	const store = useStore();

	const { startLocationId, endLocationId, setState } = useHomeStore();
	const { wizardData, setWizardData } = useBookingWizardDataStore(x => ({
		wizardData: x.wizardData,
		setWizardData: x.setWizardData,
	}));

	const { isLoading, routes } = journeyElementsStore();
	const [locationsToShow, setLocationsToShow] = React.useState<LocationEntity[]>([]);

	const [currentRoute, setCurrentRoute] = React.useState<RouteEntity>();

	usePassengerTypes();

	useEffect(() => {
		// Set persisted locations
		const newData = new BookingWizardData({ ...wizardData });
		newData.fromLocationId = startLocationId;
		newData.toLocationId = endLocationId;
		// Note: Locations will be overriden by TripSelector if not valid
		setWizardData(newData);
	}, []);

	// useEffect(() => {
	// 	if (isNotNullOrUndefined(orgStore.selectedOrganisation)) {
	// 		if (!orgStore.selectedOrganisation.hasFerries) {
	// 			navigate('/upcoming-events');
	// 		}
	// 	}
	// }, [orgStore.selectedOrganisation]);

	const { tabSelected } = wizardData;
	const onVehicleTab = tabSelected === 'vehicle';
	const onPassengerTab = tabSelected === 'passenger';

	// Guard the passenger tab so a direct `?type=passenger` link can't bypass the hidden button.
	// showPassengerBookingOption is the single source of truth: staff always keep the option when
	// passenger-only bookings are enabled, customers additionally require the customer flow flag.
	React.useEffect(() => {
		if (onPassengerTab && !appConfigStore.showPassengerBookingOption) {
			navigate('/home');
		}
	}, [onPassengerTab]);

	// React.useEffect(() => {
	// 	const organisation = orgStore.selectedOrganisation;
	// 	if (organisation !== null) {
	// 		const routeIds = locationAndRoutesStore.routes
	// 			.filter(r => r.organisations.some(y => y.organisationId === organisation.id))
	// 			.map(r => r.id);
	// 		setLocationsToShow(
	// 			locationAndRoutesStore.locations.filter(location => {
	// 				return (location.departureRoutes.some(r => routeIds.includes(r.id))
	// 					|| location.destinationRoutes.some(r => routeIds.includes(r.id)));
	// 			}),
	// 		);
	// 	}
	// }, [orgStore.selectedOrganisation]);

	React.useEffect(() => {
		if (store.loggedIn && !store.isStaff && wizardData.userId === '') {
			const newData = new BookingWizardData({ ...wizardData });
			newData.userId = store.userId ?? '';
			setWizardData(newData);
		}
	}, [store.loggedIn, store.isStaff, wizardData.userId]);

	// TODO fix multi-stop maps - temp solution
	React.useEffect(() => {
		// Only return a route if it has matching departure and destination locations
		// OR if the departure location is before the destination location in the order of the stops on the route
		const newRoute = routes.filter(r => {
			const startStop = r.stops.find(s => s.locationId === startLocationId);
			const endStop = r.stops.find(s => s.locationId === endLocationId);
			return (
				(r.departureId === startLocationId && r.destinationId === endLocationId) ||
				(isNotNullOrUndefined(startStop) && isNotNullOrUndefined(endStop) && startStop.order < endStop.order)
			);
		});

		// We only want to set the route and show the image/map if there is only one route matching the departure and destination locations
		if (newRoute.length === 1 && isNotNullOrUndefined(newRoute)) {
			setCurrentRoute(newRoute[0]);
		}
	}, [startLocationId, endLocationId, wizardData.fromLocationId, wizardData.toLocationId, routes]);

	const { isMobile } = useDeviceDetect();

	if (isMobile) {
		navigate('/booking-wizard/search');
		return <></>;
	}

	if (isLoading) {
		return <LottieSpinner />;
	}

	const cargoIcon = (
		<span className="material-symbols-outlined booking-type__icon vehicle" style={{ color: 'black' }}>
			directions_car
		</span>
	);
	const passengerIcon = <Icon name="person" classname="booking-type__icon person" useMaterialIcons />;

	return (
		<>
			<div className="home-page__background">
				<img className="background-img" src={themeStore.homepageBackgroundImageURL} alt="bg-img" />
				<div className="colour-overlay" />
			</div>
			<div className="home-search-page__container">
				<div className="main-content__container">
					<div className="search-form__container">
						<div className="search-form__top-container">
							<div className="search-form__header">
								<h5>
									<If condition={type === 'cargo'}>
										{cargoIcon}
										<FormattedMessage
											id="homepage-search__vehicle-heading__label"
											defaultMessage="Taking a vehicle"
										/>
									</If>
									<If condition={type === 'passenger'}>
										{passengerIcon}
										<FormattedMessage
											id="homepage-search__passenger-heading__label"
											defaultMessage="Walk on passenger"
										/>
									</If>
									<If condition={type === 'freight'}>
										<FreightIcon />
										<FormattedMessage
											id="homepage-search__freight-heading__label"
											defaultMessage="Freight shipping"
										/>
									</If>
								</h5>
								<Button
									className="change-booking-type-btn"
									onClick={() => {
										navigate('/home');
									}}
									colors={Colors.Secondary}
									display={Display.Text}
								>
									<FormattedMessage
										id="homepage-search__change-booking-type__button"
										defaultMessage="Change booking type"
									/>
								</Button>
							</div>
							<TripDetails
								model={{
									tripTypeTabSelected: wizardData.tripType,
									fromLocation: wizardData.fromLocationId,
									toLocation: wizardData.toLocationId,
									startDate:
										wizardData.departureTrip === false
											? (wizardData.associatedTripDateTime ?? wizardData.startDate)
											: wizardData.startDate,
									endDate:
										wizardData.departureTrip === true
											? (wizardData.associatedTripDateTime ?? wizardData.endDate)
											: wizardData.endDate,
									route: new RouteEntity(),
									wizardData: wizardData,
								}}
								departureTrip={wizardData.departureTrip}
								typeSelected={wizardData.tripType}
								onTabSelected={newTripType => {
									const newData = new BookingWizardData({ ...wizardData });
									newData.tripType = newTripType;
									newData.userSelectedTripType = true;
									newData.automaticallyApplyAddOnsToBothDirections = true;
									if (newData.tripType === 'one way') {
										newData.returningTicketId = '';
										newData.returningJourney = undefined;
										newData.automaticallyApplyAddOnsToBothDirections = false;
									}
									setWizardData(newData);
								}}
								onDatesChanged={(date, departureDate, updateOtherDate) => {
									if (date !== undefined) {
										const newData = new BookingWizardData({ ...wizardData });
										if (departureDate || updateOtherDate) {
											newData.startDate = date;
											newData.ticketSelectionStartDate = date;
										}
										if (!departureDate || updateOtherDate) {
											newData.endDate = date;
											newData.ticketSelectionEndDate = date;
										}
										setWizardData(newData);
									}
								}}
								onUpdateRouteDetails={(fromLocation, toLocation) => {
									setState({ startLocationId: fromLocation, endLocationId: toLocation });

									const newData = new BookingWizardData({ ...wizardData });
									newData.fromLocationId = fromLocation;
									newData.toLocationId = toLocation;

									// Reset because additional booking options may not be
									// available in the new route/ferry
									if (
										newData.innerWizardData instanceof VehicleBookingWizardData ||
										newData.innerWizardData instanceof PassengerBookingWizardData
									) {
										newData.innerWizardData.departingTripOptions = [];
										newData.innerWizardData.returningTripOptions = [];
									}

									setWizardData(newData);
								}}
							/>
							<div className="dashed-border" />
						</div>
						<div className="search-form__bottom-container">
							<If
								condition={
									onPassengerTab || (onVehicleTab && appConfigStore.vehicleHasPassengersEnabled)
								}
							>
								<div className="search-form-passengers__container">
									<PassengerTypeInputs location={location} />
								</div>
							</If>
							<Button
								className="search-trips__btn"
								onClick={() => {
									trackTripSearch({
										booking_type: analyticsBookingType(wizardData.tabSelected),
										trip_type: wizardData.tripType,
										route: analyticsRouteLabel(wizardData.fromLocationId, wizardData.toLocationId),
										from_location_id: wizardData.fromLocationId,
										to_location_id: wizardData.toLocationId,
										sailing_date: toAnalyticsDate(wizardData.startDate),
										return_date:
											wizardData.tripType === 'return'
												? toAnalyticsDate(wizardData.endDate)
												: undefined,
									});
									navigate('/booking-wizard/tickets');
								}}
								display={Display.Solid}
								sizes={Sizes.Large}
								colors={Colors.Secondary}
								newStyles
							>
								<FormattedMessage
									id="homepage-search__search-trips__button"
									defaultMessage="Search trips"
								/>
							</Button>
						</div>
					</div>
					{/* TODO fix multi-stop maps - temp solution */}
					<If
						condition={
							isNotNullOrUndefined(currentRoute) && stringNotEmpty(currentRoute.mapCanvasCoordinates)
						}
					>
						<RouteMap
							currentRoute={currentRoute?.id}
							locations={locationsToShow}
							routes={routes}
							isMobile={isMobile}
						/>
					</If>
					<If
						condition={
							isNotNullOrUndefined(currentRoute) &&
							isNotNullOrUndefined(currentRoute.mapId) &&
							stringIsEmpty(currentRoute.mapCanvasCoordinates)
						}
					>
						<div className="map__container" style={{ display: isLoading ? 'none' : undefined }}>
							<img
								className="event-detailed-view__supporting-image"
								src={`/api/files/${currentRoute?.mapId}`}
								key={currentRoute?.mapId}
								alt=""
							/>
						</div>
					</If>
				</div>
			</div>
		</>
	);
}

export default observer(HomeSearch);
